import Link from "next/link";
import DeviceOnly from "@/components/DeviceOnly";
import Image from "next/image";
import { social_media_path } from "@core/helpers/sosialMedias";
import { to_nested } from "@core/helpers/array";
import { resolve_menu_url } from "@/helpers/blogUrl";
import Title from "@/components/Title";
import Goup from "@/components/Goup";
import EnamadSeal from "./_enamadSeal";
import Accordion from "@/components/ui/accordion";
import FooterNavigation from "../FooterNavigation";
import FooterNavigationBlog from "../FooterNavigationBlog";
import FooterNavigationSellerAcademy from "../FooterNavigationSellerAcademy";
import FooterNavigationAds from "../FooterNavigationAds";
import is_seller_academy_path from "../Header/_isSellerAcademyPath";
import is_ads_path from "../Header/_isAdsPath";
import useFooterNavContext, { FOOTER_NAV_CONTEXT } from "@/hooks/useFooterNavContext";
import GuaranteeProduct from "@/components/guaranteeProduct";
import Collapse from "@/components/ui/Collapse";
import { is_pwa_install_url } from "@/helpers/pwaInstall";
import PwaInstallItem from "./_pwaInstallItem";

export default function Footer({ widget, pathname }) {

  const isBlogPage =
    pathname.startsWith("/blog") || pathname.startsWith("/-blog");
  // تک‌مقاله نوار اکشن فیکس خودش را دارد و نویگیشن فوتر مجله نمی‌گیرد
  const isSingleBlog = pathname.startsWith("/blog/");
  const isSellerAcademy = is_seller_academy_path(pathname);
  const isSingleProduct = pathname.startsWith("/product/");
  const isCart = pathname.startsWith("/cart");
  const isCheckOut = pathname.startsWith("/checkout");
  const isCategoryMobile = pathname === "/mobile-category/";
  const isAccount = pathname.startsWith("/account");
  // صفحه‌ی حساب کاربری نویگیشن همان بخشی (مجله / سلر آکادمی) را نگه می‌دارد که کاربر از آن آمده
  const isAds = is_ads_path(pathname);
  const nav_context = useFooterNavContext({ isSellerAcademy, isBlogPage, isAccount, isAds });
  // فوتر اصلی سایت در همه‌ی صفحه‌های Ads (دسکتاپ و موبایل) نمایش داده می‌شود
  const hide_footer_on_mobile =
    isCart || isAccount || isCategoryMobile || isCheckOut;

  const mobile_bottom_offset =
    "calc(var(--footer-nav-height, 0px) + var(--page-fixed-footer-height, 0px))";

  const widgets = Object.fromEntries(
    (widget?.widgets ?? []).map((item) => [item.widget_component_key, item])
  );

  const mainTitle_widget = widgets["footer-main-title"];
  const support_widget = widgets["support"];
  const guarantee_widget = widgets["footer-guarantee-products"];
  const footerMenu_widget = widgets["footer-menu"];
  const socialMedia_widget = widgets["social-media-footer"];
  const footerMenuItems = to_nested(
    footerMenu_widget?.mega_menu_data?.value
  );
  // آیتم «وب اپلیکیشن ژومنت» در پنل داخل منوی فوتر است ولی در طرح به‌صورت کارت جدا بین ردیف‌ها می‌آید
  const pwa_install_link = footerMenuItems
    ?.flatMap((item) => item?.children ?? [])
    .find((link) => is_pwa_install_url(link?.link?.url));
  return (
    <>
      <footer
        style={{ "--footer-mobile-offset": mobile_bottom_offset }}
        className={`bg-linear-225 from-jmt-neutral-950 to-jmt-neutral-900 py-4 lg:py-6 mb-(--footer-mobile-offset) lg:mb-0 ${hide_footer_on_mobile ? "hidden lg:block" : ""}`}>
        <div className="container">

          {hide_footer_on_mobile ? "" :
            <DeviceOnly on="mobile">
            <div dir="rtl" className="block lg:hidden">

              {/* logo & goup */}
              <div className="row-1 flex items-start justify-between border-b border-jmt-neutral-700 pb-4">
                <div className="w-[119px] h-8">
                  <Image
                    src="/images/logo/white.png"
                    unoptimized
                    alt="لوگو ژومنت"
                    className="object-contain w-full h-full"
                    width={119}
                    height={32}
                  />
                </div>
                <Goup />
              </div>

              {/* support */}
              <div className="row-2 flex flex-col border-b border-jmt-neutral-700 gap-2 py-4">
                <div className="flex items-center justify-start gap-1">
                  <span className="text-xs text-jmt-text-dark-mode">{support_widget?.fields?.["phone-support"]?.title} : </span>
                  <span className="text-xs text-jmt-text-dark-mode" dir="ltr">{support_widget?.fields?.["phone-support"]?.slug}</span>
                </div>
                <div className="flex items-center justify-start gap-1">
                  <Link href={`mailto:${support_widget?.fields?.["email-support"]?.slug}`}>
                    <span className="text-xs text-jmt-text-dark-mode">{support_widget?.fields?.["email-support"]?.title} : </span>
                    <span className="text-xs text-jmt-text-dark-mode" dir="ltr">{support_widget?.fields?.["email-support"]?.slug}</span>
                  </Link>
                </div>
                <div className="flex items-center justify-start">
                  <span className="text-xs text-jmt-text-dark-mode">{support_widget?.fields?.["text-support"]}</span>
                </div>
              </div>

              {/* guarantee */}
              <GuaranteeProduct guarantee_widget={guarantee_widget} className="row-3 border-b border-jmt-neutral-700 text-jmt-text-dark-mode gap-x-5 py-4" />

              {/* pwa install */}
              {pwa_install_link && (
                <PwaInstallItem
                  title={pwa_install_link?.title}
                  className="row-3-pwa border-b border-jmt-neutral-700 py-4"
                />
              )}

              {/* mega menu & social media */}
              <div className="row-4 flex flex-col">

                {/*  mega menu */}
                {footerMenuItems?.map((item, index) => (

                  <div key={index} className="py-4 border-b border-jmt-neutral-700">
                    <Accordion
                      title={<Title type={socialMedia_widget?.fields?.title?.type}> {item?.title}</Title>}
                      titleClassName="font-bold text-sm text-jmt-text-dark-mode text-right"
                      headerClassName="flex items-center justify-between w-full"
                      iconClassname="text-jmt-text-dark-mode"
                    >
                      <ul className="flex flex-col space-y-3 mt-2">
                        {item?.children
                          ?.filter((link) => !is_pwa_install_url(link?.link?.url))
                          .map((link) => (
                            <li key={link?.id}>
                              <Link
                                href={resolve_menu_url(link?.link?.url)}
                                className="text-xs text-jmt-text-dark-mode"
                              >
                                {link?.title}
                              </Link>
                            </li>
                          ))}
                      </ul>
                    </Accordion>
                  </div>
                ))}

                {/* social media */}
                <div className="row-5 border-b border-jmt-neutral-700 py-4">
                  <Title
                    type={socialMedia_widget?.fields?.title?.type}
                    className="text-sm font-bold text-jmt-text-dark-mode mb-2" >
                    {socialMedia_widget?.fields?.title?.title}
                  </Title>

                  <ul className="flex items-center space-x-5">
                    {socialMedia_widget?.fields?.repeatable_fields?.map(
                      (item, index) => (
                        <li key={index}>
                          <Link
                            aria-label={item?.["social-media"]?.type}
                            href={social_media_path(
                              item?.["social-media"]?.type,
                              item?.["social-media"]?.slug,
                            )}
                            target="_blank"
                            className="flex items-center text-jmt-text-dark-mode duration-200"
                          >

                            {item?.["social-media"]?.type === "instagram" ? (

                              <div className="flex items-center justify-center w-9 h-9">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M17.5 6.5H17.51M7 2L17 2C19.7614 2 22 4.23858 22 7V17C22 19.7614 19.7614 22 17 22H7C4.23858 22 2 19.7614 2 17L2 7C2 4.23858 4.23858 2 7 2ZM16 11.3698C16.1234 12.2021 15.9812 13.052 15.5937 13.7988C15.2062 14.5456 14.5931 15.1512 13.8416 15.5295C13.0901 15.9077 12.2384 16.0394 11.4077 15.9057C10.5771 15.7721 9.80971 15.3799 9.21479 14.785C8.61987 14.1901 8.22768 13.4227 8.09402 12.592C7.96035 11.7614 8.09202 10.9097 8.47028 10.1582C8.84854 9.40667 9.45414 8.79355 10.2009 8.40605C10.9477 8.01856 11.7977 7.8764 12.63 7.99981C13.4789 8.1257 14.2648 8.52128 14.8716 9.12812C15.4785 9.73496 15.8741 10.5209 16 11.3698Z" stroke="#E0E0E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>

                            ) : item?.["social-media"]?.type === "email" ? (

                              <div className="flex items-center justify-center w-9 h-9">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M22 7L13.009 12.727C12.7039 12.9042 12.3573 12.9976 12.0045 12.9976C11.6517 12.9976 11.3051 12.9042 11 12.727L2 7M4 4H20C21.1046 4 22 4.89543 22 6V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6C2 4.89543 2.89543 4 4 4Z" stroke="#E0E0E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>

                            ) : item?.["social-media"]?.type === "telegram" ? (

                              <div className="flex items-center justify-center w-9 h-9">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3.22615 10.9036L15.1395 5.85766C16.3155 5.33205 20.3036 3.65008 20.3036 3.65008C20.3036 3.65008 22.1443 2.91422 21.9909 4.70131C21.9398 5.43717 21.5307 8.01267 21.1217 10.7984L19.8435 19.0506C19.8435 19.0506 19.7412 20.2595 18.872 20.4697C18.0028 20.68 16.5711 19.7339 16.3155 19.5236C16.111 19.3659 12.4807 17.0007 11.1513 15.8443C10.7934 15.529 10.3844 14.8982 11.2025 14.1624C13.0431 12.4278 15.2417 10.2728 16.5711 8.90622C17.1847 8.27548 17.7982 6.80376 15.2417 8.59085L8.03238 13.5842C8.03238 13.5842 7.2143 14.1098 5.6804 13.6367C4.14649 13.1637 2.35694 12.533 2.35694 12.533C2.35694 12.533 1.12981 11.7445 3.22615 10.9036Z" fill="#E0E0E0" />
                                </svg>
                              </div>


                            ) : ""}
                          </Link>
                        </li>
                      ),
                    )}
                  </ul>
                </div>

              </div>
            </div>
            </DeviceOnly>
          }

          <DeviceOnly on="desktop">
          <div className="hidden lg:block">
            {/* logo & goup */}
            <div className="row-1 flex items-start justify-between pb-6">
              <div className="w-[148px] h-10 p-0">
                <Image
                  src="/images/logo/white.png"
                  unoptimized
                  alt="لوگو ژومنت"
                  className="object-contain w-full h-full"
                  width={119}
                  height={32} />
              </div>
              <Goup />
            </div>

            {/* support */}
            <div className="row-2 flex items-baseline border-b border-jmt-neutral-700 pb-6">
              <div className="flex items-center justify-between gap-1">
                <Link href={`tel:${support_widget?.fields?.["phone-support"]?.slug}`} target="_blank">
                  <span className="text-xs text-jmt-text-dark-mode">{support_widget?.fields?.["phone-support"]?.title} : </span>
                  <span className="text-xs text-jmt-text-dark-mode" dir="ltr">{support_widget?.fields?.["phone-support"]?.slug}</span>
                </Link>
              </div>
              <div className="px-4 text-neutral-300">|</div>
              <div className="flex items-center justify-between gap-1">
                <Link href={`mailto:${support_widget?.fields?.["email-support"]?.slug}`} target="_blank">
                  <span className="text-xs text-jmt-text-dark-mode">{support_widget?.fields?.["email-support"]?.title} : </span>
                  <span className="text-xs text-jmt-text-dark-mode" dir="ltr">{support_widget?.fields?.["email-support"]?.slug}</span>
                </Link>
              </div>
              <div className="px-4 text-neutral-300">|</div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-jmt-text-dark-mode">{support_widget?.fields?.["text-support"]}</span>
              </div>
            </div>

            {/* guarantee */}
            <GuaranteeProduct guarantee_widget={guarantee_widget} className="row-3 border-b border-jmt-neutral-700 text-jmt-text-dark-mode gap-x-6 py-6" />

            {/* mega menu & social media */}
            <div className="row-4 flex items-start justify-between border-b border-jmt-neutral-700 py-6">

              {/*  mega menu */}
              {footerMenuItems?.map((item, index) => (
                <div key={index}>
                  <Title
                    type={socialMedia_widget?.fields?.title?.type}
                    className="font-bold text-jmt-text-dark-mode mb-2" >
                    {item?.title}
                  </Title>

                  <ul className="flex flex-col space-y-2">
                    {item?.children?.filter((link) => !is_pwa_install_url(link?.link?.url)).map((link) => (
                      <li key={link?.id}>
                        <Link
                          href={resolve_menu_url(link?.link?.url)}
                          className="text-xs lg:text-sm text-jmt-text-dark-mode duration-200" >
                          {link?.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* social media */}
              <div>
                <Title
                  type={socialMedia_widget?.fields?.title?.type}
                  className="font-bold text-jmt-text-dark-mode mb-2" >
                  {socialMedia_widget?.fields?.title?.title}
                </Title>

                <ul className="flex items-center space-x-5">
                  {socialMedia_widget?.fields?.repeatable_fields?.map(
                    (item, index) => (
                      <li key={index}>
                        <Link
                          aria-label={item?.["social-media"]?.type}
                          href={social_media_path(
                            item?.["social-media"]?.type,
                            item?.["social-media"]?.slug,
                          )}
                          target="_blank"
                          className="flex items-center text-jmt-text-dark-mode duration-200"
                        >

                          {item?.["social-media"]?.type === "instagram" ? (

                            <div className="w-9 h-9">
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.5 6.5H17.51M7 2L17 2C19.7614 2 22 4.23858 22 7V17C22 19.7614 19.7614 22 17 22H7C4.23858 22 2 19.7614 2 17L2 7C2 4.23858 4.23858 2 7 2ZM16 11.3698C16.1234 12.2021 15.9812 13.052 15.5937 13.7988C15.2062 14.5456 14.5931 15.1512 13.8416 15.5295C13.0901 15.9077 12.2384 16.0394 11.4077 15.9057C10.5771 15.7721 9.80971 15.3799 9.21479 14.785C8.61987 14.1901 8.22768 13.4227 8.09402 12.592C7.96035 11.7614 8.09202 10.9097 8.47028 10.1582C8.84854 9.40667 9.45414 8.79355 10.2009 8.40605C10.9477 8.01856 11.7977 7.8764 12.63 7.99981C13.4789 8.1257 14.2648 8.52128 14.8716 9.12812C15.4785 9.73496 15.8741 10.5209 16 11.3698Z" stroke="#E0E0E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>

                          ) : item?.["social-media"]?.type === "email" ? (

                            <div className="w-9 h-9">
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22 7L13.009 12.727C12.7039 12.9042 12.3573 12.9976 12.0045 12.9976C11.6517 12.9976 11.3051 12.9042 11 12.727L2 7M4 4H20C21.1046 4 22 4.89543 22 6V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6C2 4.89543 2.89543 4 4 4Z" stroke="#E0E0E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>

                          ) : item?.["social-media"]?.type === "telegram" ? (

                            <div className="w-9 h-9">
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3.22615 10.9036L15.1395 5.85766C16.3155 5.33205 20.3036 3.65008 20.3036 3.65008C20.3036 3.65008 22.1443 2.91422 21.9909 4.70131C21.9398 5.43717 21.5307 8.01267 21.1217 10.7984L19.8435 19.0506C19.8435 19.0506 19.7412 20.2595 18.872 20.4697C18.0028 20.68 16.5711 19.7339 16.3155 19.5236C16.111 19.3659 12.4807 17.0007 11.1513 15.8443C10.7934 15.529 10.3844 14.8982 11.2025 14.1624C13.0431 12.4278 15.2417 10.2728 16.5711 8.90622C17.1847 8.27548 17.7982 6.80376 15.2417 8.59085L8.03238 13.5842C8.03238 13.5842 7.2143 14.1098 5.6804 13.6367C4.14649 13.1637 2.35694 12.533 2.35694 12.533C2.35694 12.533 1.12981 11.7445 3.22615 10.9036Z" fill="#E0E0E0" />
                              </svg>
                            </div>


                          ) : ""}
                        </Link>
                      </li>
                    ),
                  )}
                </ul>
              </div>

            </div>
          </div>
          </DeviceOnly>

          <div className="py-4 lg:py-6">

            {/* متن سئوی فوتر عنوان صفحه نیست و هیچ‌وقت نباید h1 شود؛ h1 هر
                صفحه در خود آن صفحه رندر می‌شود */}
            <h2 className="text-base lg:text-lg font-bold text-jmt-text-dark-mode mb-2">
              {mainTitle_widget?.fields?.title}
            </h2>

            <Collapse
              collapsedHeight={100}
              expandText="مشاهده بیشتر"
              collapseText="بستن"
              expandButtonClassName="text-jmt-neutral-400 text-xs lg:text-sm font-bold mt-2"
              collapseButtonClassName="text-jmt-neutral-400 text-xs lg:text-sm font-bold"
              animationDuration={300}
            >

              <div className="text-editor-main-title-footer" dangerouslySetInnerHTML={{
                __html: mainTitle_widget?.fields?.description
              }} />

            </Collapse>
          </div>

          {hide_footer_on_mobile ? "" :
            <DeviceOnly on="mobile">
            <div dir="rtl" className="block lg:hidden">
              {/* enamad & copy write */}
              <div className="row-7 pb-4 text-jmt-text-dark-mode flex flex-col items-center justify-center">

                <div className="flex items-center justify-center mb-4">
                  {/* enamad */}
                  <EnamadSeal />
                </div>

                <div className="flex md:hidden flex-col justify-center items-center">
                  <div className="text-[10px] text-center mb-2">
                    تولید و توسعه نرم‌افزار ژومنت توسط   <a target="_blank" href="https://shakheara.com" className="text-jmt-green-500"> شرکت طراحی و توسعه شاخه آرا </a> انجام می‌شود
                  </div>
                  <div className="w-15 h-px bg-jmt-text-dark-mode mb-2" />
                  <div className="text-[10px] text-center">
                    تمامی حقوق مادی و معنوی این سایت متعلق به ژومنت می‌باشد.
                  </div>
                </div>

                <div className="hidden md:flex items-center justify-between space-x-2">
                  <div className="text-xs">
                    تولید و توسعه نرم‌افزار ژومنت توسط   <a target="_blank" href="https://shakheara.com" className="text-jmt-green-500"> شرکت طراحی و توسعه شاخه آرا </a> انجام می‌شود
                  </div>
                  <div>|</div>
                  <div className="text-xs">
                    تمامی حقوق مادی و معنوی این سایت متعلق به ژومنت می‌باشد.
                  </div>
                </div>

              </div>
            </div>
            </DeviceOnly>
          }

          <DeviceOnly on="desktop">
          <div className="hidden lg:block">
            {/* enamad & copy write */}
            <div className="row-6 text-jmt-text-dark-mode flex flex-col items-center justify-center space-y-3">

              <div className="flex items-center justify-between space-x-6">
                {/* enamad */}
                <EnamadSeal />
              </div>

              <div className="flex items-center justify-between space-x-2">
                <div className="text-xs">
                  تولید و توسعه نرم‌افزار ژومنت توسط   <a target="_blank" href="https://shakheara.com" className="text-jmt-green-500"> شرکت طراحی و توسعه شاخه آرا </a> انجام می‌شود
                </div>
                <div>|</div>
                <div className="text-xs">
                  تمامی حقوق مادی و معنوی این سایت متعلق به ژومنت می‌باشد.
                </div>
              </div>
            </div>
          </div>
          </DeviceOnly>

        </div>
      </footer>

      <DeviceOnly on="mobile">
      <div className="block lg:hidden">
        {nav_context === FOOTER_NAV_CONTEXT.ADS ? <FooterNavigationAds />
          : nav_context === FOOTER_NAV_CONTEXT.SELLER_ACADEMY ? <FooterNavigationSellerAcademy />
          : nav_context === FOOTER_NAV_CONTEXT.BLOG ? (isSingleBlog ? "" : <FooterNavigationBlog />)
          : isSingleProduct || isCheckOut ? "" : <FooterNavigation />}
      </div>
      </DeviceOnly>
    </>
  );
}
